import {
  connectBaseApi,
  locationClient,
  createRpcQuery,
  createRpcMutation,
  ConnectProto,
} from "@racoon/api";
import { getEnumName } from "@racoon/lib";

// LocationService is generic over LocationType — every kind of location goes
// through the same endpoints, so `types` in the list request is what narrows it.
export const ALL_LOCATION_TYPES = ConnectProto.LocationTypeSchema.values
  .map((value) => value.number as ConnectProto.LocationType)
  .filter((type) => type !== ConnectProto.LocationType.UNDEFINED);

// One cache tag per location type, keyed by the protobuf JSON name
// ("LOCATION_TYPE_DEPOT"), so a mutation on a depot only refreshes the lists
// that can contain depots — and the same holds for every other type.
export const typeTag = (type: ConnectProto.LocationType) =>
  ({
    type: "Location",
    id: getEnumName(ConnectProto.LocationTypeSchema, type) ?? "LOCATION_TYPE_UNDEFINED",
  }) as const;

const ALL_TYPE_TAGS = ALL_LOCATION_TYPES.map(typeTag);

// A payload without a type has to be assumed to have touched anything.
const tagsForType = (type?: ConnectProto.LocationType) =>
  type == null || type === ConnectProto.LocationType.UNDEFINED ? ALL_TYPE_TAGS : [typeTag(type)];

const locationsGrpc = connectBaseApi.injectEndpoints({
  endpoints: (build) => ({
    createLocation: createRpcMutation(build, locationClient, "createLocation", {
      invalidatesTags: (_r, _e, { type }) => tagsForType(type),
    }),
    listLocations: createRpcQuery(build, locationClient, "listLocations", {
      providesTags: (_r, _e, { types }) =>
        (types?.length ? types : ALL_LOCATION_TYPES).map(typeTag),
    }),
    getLocationById: createRpcQuery(build, locationClient, "getLocationById", {
      providesTags: (_r, _e, { id }) => [{ type: "Location", id: String(id) }],
    }),
    // An update can move a location between types, so both the old and the new
    // type's lists have to be refreshed — the payload only carries the new one.
    updateLocation: createRpcMutation(build, locationClient, "updateLocation", {
      invalidatesTags: (_r, _e, { id }) => [
        { type: "Location" as const, id: String(id) },
        ...ALL_TYPE_TAGS,
      ],
    }),
    // The organizations' own locations plus those of the organizations picked in
    // their settings — which own the disposal sites their trucks unload at.
    listDestinationLocations: createRpcQuery(build, locationClient, "listDestinationLocations", {
      providesTags: (_r, _e, { types }) =>
        (types?.length ? types : ALL_LOCATION_TYPES).map(typeTag),
    }),
    listDisposalSites: createRpcQuery(build, locationClient, "listDisposalSites", {
      providesTags: [typeTag(ConnectProto.LocationType.DISPOSAL_SITE)],
    }),
  }),
  overrideExisting: false,
});

export const {
  useListLocationsQuery,
  useGetLocationByIdQuery,
  useCreateLocationMutation,
  useUpdateLocationMutation,
  useListDestinationLocationsQuery,
  useListDisposalSitesQuery,
} = locationsGrpc;
